// src/hooks/useDomainTypes.tsx
/**
 * Hook for managing domain/cancer type selection.
 * Fetches available types, patient's current selection, AI-suggested domains,
 * and active domains (auto-detected with tables already created).
 */
import { createData, fetchData } from "api/apiHelpers";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import logger from "utils/logger";

type DomainType = { value: string; label: string; group?: string };

interface SuggestedDomain {
  domain: string;
  confidence: number;
  document_count: number;
}

interface SuggestionsResponse {
  suggestions: SuggestedDomain[];
  patient_id: number;
}

interface ActiveDomainsResponse {
  active_domains: string[];
  tables: Record<string, Record<string, { id: number; status: string }>>;
  processing: boolean;
  has_errors?: boolean;
  /** Retirement flag (066 D / 4.0): when false, hide the legacy Baseline/History sections. */
  legacy_cancer_tables_enabled?: boolean;
}

const useDomainTypes = () => {
  const { id } = useParams();
  const [domainTypes, setDomainTypes] = useState<DomainType[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [suggestedDomains, setSuggestedDomains] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStaleProcessing, setIsStaleProcessing] = useState(false);
  const [hasExtractionErrors, setHasExtractionErrors] = useState(false);
  // Retirement flag (066 D / 4.0): default true; the sidebar hides legacy sections when false.
  const [legacyTablesEnabled, setLegacyTablesEnabled] = useState(true);

  // Track active domains polling
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch available domain types, patient's selection, and AI suggestions
  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        // Fetch available domain types
        const types = await fetchData<DomainType[]>("/domain-types/");
        setDomainTypes(types);

        // Fetch patient's current selection
        const patientData = await fetchData<{ domains: string[] }>(
          `/patients/${id}/`
        );
        setSelectedDomains(patientData.domains || []);

        // Fetch active domains (domains with tables already created)
        await refreshActiveDomains();

        // Fetch AI-suggested domains
        try {
          const suggestions = await createData<{ patient_id: number }, SuggestionsResponse>(
            "/detect-domains/",
            { patient_id: Number(id) }
          );
          // Extract domain names from suggestions with confidence > 0.5
          const suggested = suggestions.suggestions
            .filter((s) => s.confidence > 0.5)
            .map((s) => s.domain);
          setSuggestedDomains(suggested);
        } catch (err) {
          // Suggestions are optional, don't block on error
          logger.debug("Could not fetch domain suggestions:", err);
          setSuggestedDomains([]);
        }
      } catch (error) {
        logger.error("Error fetching domain types or patient data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchAll();
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [id]);

  // Maximum number of domains a patient can have selected (safety limit)
  const MAX_DOMAINS = 10;

  // Fetch active domains and merge with selected
  const refreshActiveDomains = useCallback(async (): Promise<ActiveDomainsResponse | null> => {
    if (!id) return null;
    try {
      const response = await fetchData<ActiveDomainsResponse>(
        `/patients/${id}/active-domains/`
      );
      
      // Update processing state and error flags
      setIsProcessing(response.processing);
      setHasExtractionErrors(response.has_errors ?? false);
      setLegacyTablesEnabled(response.legacy_cancer_tables_enabled ?? true);
      
      // Merge active domains with selected (auto-select active domains)
      // Apply MAX_DOMAINS limit to prevent runaway selection
      if (response.active_domains.length > 0) {
        setSelectedDomains((prev) => {
          const merged = new Set([...prev, ...response.active_domains]);
          const mergedArray = Array.from(merged);
          // Limit to MAX_DOMAINS to prevent performance issues
          if (mergedArray.length > MAX_DOMAINS) {
            logger.warn(`[Domains] Limiting from ${mergedArray.length} to ${MAX_DOMAINS} domains`);
            return mergedArray.slice(0, MAX_DOMAINS);
          }
          
          // If new domains were added, persist to backend
          if (mergedArray.length > prev.length) {
            logger.debug("[Domains] New domains detected, saving:", mergedArray);
            createData(`/patients/${id}/domains/`, { domains: mergedArray }).catch(
              (err) => logger.error("Failed to save domains:", err)
            );
          }
          
          return mergedArray;
        });
      }
      
      return response;
    } catch (err) {
      logger.debug("Could not fetch active domains:", err);
      return null;
    }
  }, [id]);

  // Poll for active domains until processing completes.
  //
  // Production-grade adaptive backoff:
  // - Starts at 2s, ramps to 10s after 10 polls, then 30s after 40 polls.
  // - Max duration: ~7 minutes (covers large extractions like SkinCancerEventTable
  //   which can take 2-3 min alone, plus document processing overhead).
  // - If still processing after timeout, sets isStaleProcessing so the UI
  //   can offer a "Retry" button instead of spinning forever.
  const pollActiveDomains = useCallback(async (
    maxAttempts = 50,
    initialIntervalMs = 2000,
  ): Promise<boolean> => {
    if (!id) return false;
    
    setIsProcessing(true);
    setIsStaleProcessing(false);
    
    return new Promise((resolve) => {
      let attempts = 0;
      
      const getInterval = (attempt: number): number => {
        // Adaptive backoff: 2s → 5s → 10s → 30s
        if (attempt < 10) return initialIntervalMs;        // 0-10: every 2s
        if (attempt < 20) return 5000;                      // 10-20: every 5s
        if (attempt < 40) return 10000;                     // 20-40: every 10s
        return 30000;                                       // 40+: every 30s
      };
      
      const poll = async () => {
        attempts++;
        logger.debug(`[Domains] Polling attempt ${attempts}/${maxAttempts}`);
        const response = await refreshActiveDomains();
        
        if (response && !response.processing) {
          // Processing complete
          setIsProcessing(false);
          setIsStaleProcessing(false);
          logger.debug("[Domains] Processing complete, active domains:", response.active_domains);
          resolve(true);
          return;
        }
        
        if (attempts < maxAttempts) {
          // Continue polling with adaptive interval
          const interval = getInterval(attempts);
          pollingRef.current = setTimeout(poll, interval);
        } else {
          // Timeout — mark as stale so the UI can show a retry button
          setIsProcessing(false);
          setIsStaleProcessing(true);
          logger.warn("[Domains] Polling timeout — processing appears stale");
          resolve(false);
        }
      };
      
      poll();
    });
  }, [id, refreshActiveDomains]);

  // Refresh suggestions (called after document upload)
  const refreshSuggestions = useCallback(async () => {
    if (!id) return;
    try {
      const suggestions = await createData<{ patient_id: number }, SuggestionsResponse>(
        "/detect-domains/",
        { patient_id: Number(id) }
      );
      const suggested = suggestions.suggestions
        .filter((s) => s.confidence > 0.5)
        .map((s) => s.domain);
      setSuggestedDomains(suggested);
    } catch (err) {
      logger.debug("Could not refresh domain suggestions:", err);
    }
  }, [id]);

  // Handle domain selection + persist (with limit)
  // Returns true if selection was limited (for UI warning)
  const handleDomainSelectionChange = async (selected: string[]): Promise<{ limited: boolean; max: number }> => {
    // Apply MAX_DOMAINS limit
    const wasLimited = selected.length > MAX_DOMAINS;
    const limitedSelection = wasLimited 
      ? selected.slice(0, MAX_DOMAINS) 
      : selected;
    
    if (wasLimited) {
      logger.warn(`[Domains] User tried to select ${selected.length} domains, limiting to ${MAX_DOMAINS}`);
    }
    
    setSelectedDomains(limitedSelection);
    try {
      await createData(`/patients/${id}/domains/`, { domains: limitedSelection });
    } catch (error) {
      logger.error("Error saving domain selection:", error);
    }
    
    return { limited: wasLimited, max: MAX_DOMAINS };
  };

  // Unstick + optionally retry extraction for this patient.
  // Calls POST /patients/{id}/retry-extraction/
  const retryExtraction = useCallback(async (): Promise<boolean> => {
    if (!id) return false;
    try {
      setIsStaleProcessing(false);
      setIsProcessing(true);
      await createData(`/patients/${id}/retry-extraction/`, { retry: true });
      // Start polling again with fresh counters
      pollActiveDomains();
      return true;
    } catch (err) {
      logger.error("[Domains] Retry extraction failed:", err);
      setIsProcessing(false);
      return false;
    }
  }, [id, pollActiveDomains]);

  return {
    domainTypes,
    selectedDomains,
    suggestedDomains,
    isLoading,
    isProcessing,
    isStaleProcessing,
    hasExtractionErrors,
    legacyTablesEnabled,
    handleDomainSelectionChange,
    refreshSuggestions,
    refreshActiveDomains,
    pollActiveDomains,
    retryExtraction,
  };
};

export default useDomainTypes;
